import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar, Building } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
} from '@jira-clone/ui'
import { useBankDetails, useBankDetailMutations } from '../../shared/hooks/useBankDetails'
import { getApiErrorMessage } from '../../shared/utils/apiError'

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  SAVINGS: 'Savings',
  CURRENT: 'Current',
  OVERDRAFT: 'Overdraft',
}

interface DraftState {
  id?: string
  accountName: string
  accountNumber: string
  bankName: string
  branch: string
  ifsc: string
  upi: string
  accountType: 'SAVINGS' | 'CURRENT' | 'OVERDRAFT'
  currency: string
  isDefault: boolean
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  accountName: '',
  accountNumber: '',
  bankName: '',
  branch: '',
  ifsc: '',
  upi: '',
  accountType: 'CURRENT',
  currency: 'INR',
  isDefault: false,
  isActive: true,
}

export default function BankDetailAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: bankDetails = [], isLoading } = useBankDetails({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove, setDefault } = useBankDetailMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (bank: any) => {
    setDraft({
      id: bank.id,
      accountName: bank.accountName,
      accountNumber: bank.accountNumber,
      bankName: bank.bankName,
      branch: bank.branch || '',
      ifsc: bank.ifsc || '',
      upi: bank.upi || '',
      accountType: bank.accountType || 'CURRENT',
      currency: bank.currency || 'INR',
      isDefault: bank.isDefault,
      isActive: bank.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.accountName.trim()) {
      toast.error('Account name is required.')
      return
    }
    if (!draft.accountNumber.trim()) {
      toast.error('Account number is required.')
      return
    }
    if (!draft.bankName.trim()) {
      toast.error('Bank name is required.')
      return
    }
    if (!draft.ifsc.trim()) {
      toast.error('IFSC code is required.')
      return
    }
    try {
      const payload = {
        accountName: draft.accountName.trim(),
        accountNumber: draft.accountNumber.trim(),
        bankName: draft.bankName.trim(),
        branch: draft.branch.trim() || '',
        ifsc: draft.ifsc.trim().toUpperCase(),
        upi: draft.upi.trim() || '',
        accountType: draft.accountType,
        currency: draft.currency,
        isDefault: draft.isDefault,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Bank detail updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Bank detail created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this bank detail' : 'create this bank detail'))
    }
  }

  const handleDelete = async (bank: any) => {
    if (!confirm(`Delete "${bank.accountName}"? This can't be undone.`)) return
    setDeletingId(bank.id)
    try {
      await remove.mutateAsync(bank.id)
      toast.success('Bank detail deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this bank detail'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (bank: any) => {
    try {
      await setStatus.mutateAsync({ id: bank.id, isActive: !bank.isActive })
      toast.success(bank.isActive ? 'Bank detail deactivated.' : 'Bank detail activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle bank detail status'))
    }
  }

  const handleSetDefault = async (bank: any) => {
    try {
      await setDefault.mutateAsync(bank.id)
      toast.success('Default bank detail updated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'set as default'))
    }
  }

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Bank Details</h2>
          <p className="text-sm text-muted-foreground">Manage company bank account details for quotations and invoices.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Bank Account
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search bank accounts..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : bankDetails.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No bank accounts yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {bankDetails.map((bank) => (
            <Card key={bank.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-muted-foreground" />
                      <Badge variant={bank.isActive ? 'default' : 'secondary'} className="text-xs">
                        {bank.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {bank.isDefault && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{bank.accountName}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
                        <span>{bank.bankName}, {bank.branch || 'Main Branch'}</span>
                        <span>A/C: {bank.accountNumber}</span>
                        <span>IFSC: {bank.ifsc}</span>
                        {bank.upi && <span>UPI: {bank.upi}</span>}
                        <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                          {ACCOUNT_TYPE_LABELS[bank.accountType] || bank.accountType}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(bank)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {bank.isDefault ? (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(bank)}
                        title="Set as Default"
                      >
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(bank)}
                      title={bank.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {bank.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(bank)}
                      disabled={deletingId === bank.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Bank Account' : 'New Bank Account'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="bd-account-name">Account Name *</Label>
                <Input
                  id="bd-account-name"
                  value={draft.accountName}
                  onChange={(e) => setDraft((d) => ({ ...d, accountName: e.target.value }))}
                  placeholder="e.g. Company Main Account"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bd-account-type">Account Type *</Label>
                <Select value={draft.accountType} onValueChange={(v) => setDraft((d) => ({ ...d, accountType: v as any }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAVINGS">Savings</SelectItem>
                    <SelectItem value="CURRENT">Current</SelectItem>
                    <SelectItem value="OVERDRAFT">Overdraft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bd-account-number">Account Number *</Label>
                <Input
                  id="bd-account-number"
                  value={draft.accountNumber}
                  onChange={(e) => setDraft((d) => ({ ...d, accountNumber: e.target.value }))}
                  placeholder="e.g. 1234567890"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bd-ifsc">IFSC Code *</Label>
                <Input
                  id="bd-ifsc"
                  value={draft.ifsc}
                  onChange={(e) => setDraft((d) => ({ ...d, ifsc: e.target.value.toUpperCase() }))}
                  placeholder="e.g. HDFC0001234"
                  maxLength={11}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="bd-bank-name">Bank Name *</Label>
                <Input
                  id="bd-bank-name"
                  value={draft.bankName}
                  onChange={(e) => setDraft((d) => ({ ...d, bankName: e.target.value }))}
                  placeholder="e.g. HDFC Bank"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bd-branch">Branch</Label>
                <Input
                  id="bd-branch"
                  value={draft.branch}
                  onChange={(e) => setDraft((d) => ({ ...d, branch: e.target.value }))}
                  placeholder="e.g. Mumbai Main Branch"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bd-upi">UPI ID</Label>
                <Input
                  id="bd-upi"
                  value={draft.upi}
                  onChange={(e) => setDraft((d) => ({ ...d, upi: e.target.value }))}
                  placeholder="e.g. company@hdfcbank"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bd-currency">Currency</Label>
                <Input
                  id="bd-currency"
                  value={draft.currency}
                  onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value }))}
                  placeholder="INR"
                />
              </div>
            </div>

            <Separator className="my-2" />

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  id="bd-default"
                  type="checkbox"
                  checked={draft.isDefault}
                  onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
                />
                <label htmlFor="bd-default" className="cursor-pointer">Set as default bank account</label>
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Bank Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}